import * as React from "react";
import {
    Button,
    DatePicker,
    InputNumber,
    Select,
    Space,
    TimePicker,
} from "antd";
import { Dayjs } from "dayjs";
import { MetadataDefinition } from "../../administration/types/Metadata";
import { FULL_DATE, ONLY_DATE, Utils } from "../../common/misc/Utils";

type Props = {
    definition: MetadataDefinition;
    value?: unknown[];
    onChange?: (value: unknown[]) => void;
    serializeDates?: boolean;
};

const MultiValueMetadataInput: React.FC<Props> = ({
    definition,
    value = [],
    onChange,
    serializeDates = false,
}) => {
    const updateValue = (index: number, nextValue: unknown) => {
        onChange?.(
            value.map((item, itemIndex) =>
                itemIndex === index ? nextValue : item,
            ),
        );
    };

    const removeValue = (index: number) => {
        onChange?.(value.filter((_, itemIndex) => itemIndex !== index));
    };

    const addValue = () => {
        const initialValue =
            definition.fieldType === "Boolean"
                ? false
                : definition.fieldType === "Number"
                  ? 0
                  : null;
        onChange?.([...value, initialValue]);
    };

    if (definition.fieldType === "Text") {
        return (
            <Select
                mode={
                    (definition.listValues?.length ?? 0) > 0
                        ? "multiple"
                        : "tags"
                }
                value={value as string[]}
                onChange={(nextValues) => onChange?.(nextValues)}
                options={definition.listValues ?? undefined}
                style={{ width: "100%" }}
                placeholder="Add values"
            />
        );
    }

    const renderScalarControl = (item: unknown, index: number) => {
        switch (definition.fieldType) {
            case "Number":
                return (
                    <InputNumber
                        value={item as number | null}
                        onChange={(nextValue) => updateValue(index, nextValue)}
                        style={{ width: "100%" }}
                    />
                );
            case "Boolean": {
                const { trueVal, falseVal } = definition.format
                    ? JSON.parse(definition.format)
                    : { trueVal: "true", falseVal: "false" };
                return (
                    <Select
                        value={item as boolean}
                        onChange={(nextValue) => updateValue(index, nextValue)}
                        options={[
                            { label: trueVal || "true", value: true },
                            { label: falseVal || "false", value: false },
                        ]}
                        style={{ width: "100%" }}
                    />
                );
            }
            case "DateTime": {
                const updateDate = (date: Dayjs | null) => {
                    if (!date) {
                        updateValue(index, null);
                    } else if (!serializeDates) {
                        updateValue(index, date);
                    } else if (definition.format === ONLY_DATE) {
                        updateValue(index, Utils.removeLocalTimeZone(date));
                    } else {
                        updateValue(index, Utils.ignoreLocalTimeZone(date));
                    }
                };
                const pickerValue =
                    item && typeof item === "object"
                        ? (item as Dayjs)
                        : Utils.formatDatePickerValue(
                              item as string,
                              undefined,
                              true,
                          );
                return definition.format === FULL_DATE ||
                    definition.format === ONLY_DATE ? (
                    <DatePicker
                        value={pickerValue}
                        format={Utils.getDateFormat(
                            definition.format === FULL_DATE,
                        )}
                        showTime={definition.format === FULL_DATE}
                        onChange={updateDate}
                        style={{ width: "100%" }}
                    />
                ) : (
                    <TimePicker
                        value={pickerValue}
                        onChange={updateDate}
                        style={{ width: "100%" }}
                    />
                );
            }
            default:
                return null;
        }
    };

    return (
        <Space direction="vertical" style={{ width: "100%" }}>
            {value.map((item, index) => (
                <Space key={index} style={{ width: "100%" }}>
                    <div style={{ minWidth: 240 }}>
                        {renderScalarControl(item, index)}
                    </div>
                    <Button
                        danger
                        type="text"
                        onClick={() => removeValue(index)}
                    >
                        Remove
                    </Button>
                </Space>
            ))}
            <Button onClick={addValue}>Add value</Button>
        </Space>
    );
};

export default MultiValueMetadataInput;
